import React from 'react';
import ReactDOM from 'react-dom';
import styled, { css, keyframes } from 'styled-components';

import { useTheme } from '../../state/theme-state';

// Animation durations in ms — must match the keyframe durations below.
const HOVER_DURATION = 2400;
const ROTOR_DURATION = 350;

/**
 * Returns a negative animation-delay (ms) that syncs the animation phase to
 * the current wall-clock time. This means any two DroneLoader instances that
 * mount close together will appear to continue the same animation, even if
 * one unmounts and a new one mounts in its place (e.g. between sequential
 * loading states such as AppState → ProjectState → ProjectReferenceProvider).
 */
function wallClockDelay(durationMs: number): string {
  return `-${Date.now() % durationMs}ms`;
}

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * fullscreen            – fixed overlay covering the entire viewport; used before login (auth check).
   * page-content          – fills #mainContent via portal; allows top navigation to remain visible.
   * page-content-with-background – fills #mainContent via portal with a themed backdrop and a
   *                         centred white card; the drone sits inside the card above any children
   *                         (e.g. a progress bar).
   */
  mode?: 'fullscreen' | 'page-content' | 'page-content-with-background';
  message?: string;
  /** CSS width for the drone SVG. Defaults to '200px'. */
  droneSize?: string;
}

const DroneLoader = ({
  mode = 'fullscreen',
  message,
  droneSize = '200px',
  children,
  ...rest
}: Props) => {
  // Compute once on mount so re-renders (e.g. message updates) never change the
  // delay values, which would generate new CSS classes and restart the animation.
  const hoverDelay = React.useRef(wallClockDelay(HOVER_DURATION)).current;
  const rotorDelay = React.useRef(wallClockDelay(ROTOR_DURATION)).current;
  const theme = useTheme();

  const drone = (
    <DroneWrap $delay={hoverDelay}>
      <DroneSvg
        $size={droneSize}
        aria-hidden="true"
        viewBox="0 0 160 120"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* ── Arms ── */}
        <g fill="none" stroke="#37424a" strokeLinecap="round" strokeWidth="5">
          <line x1="80" x2="28" y1="60" y2="40" />
          <line x1="80" x2="132" y1="60" y2="40" />
        </g>

        {/* ── Motor mounts ── */}
        <circle cx="28" cy="40" fill="#5e6a71" r="7" />
        <circle cx="132" cy="40" fill="#5e6a71" r="7" />

        {/* ── Rotor blades — left ── */}
        <RotorGroup $delay={rotorDelay} style={{ transformOrigin: '28px 40px' }}>
          <ellipse cx="14" cy="40" fill="#fed530" opacity="0.85" rx="13" ry="3.5" />
          <ellipse cx="42" cy="40" fill="#fed530" opacity="0.85" rx="13" ry="3.5" />
        </RotorGroup>

        {/* ── Rotor blades — right ── */}
        <RotorGroup $delay={rotorDelay} style={{ transformOrigin: '132px 40px' }}>
          <ellipse cx="118" cy="40" fill="#fed530" opacity="0.85" rx="13" ry="3.5" />
          <ellipse cx="146" cy="40" fill="#fed530" opacity="0.85" rx="13" ry="3.5" />
        </RotorGroup>

        {/* ── Body ── */}
        <rect fill="#37424a" height="20" rx="5" ry="5" width="30" x="65" y="50" />
        {/* camera dome */}
        <ellipse cx="80" cy="70" fill="#5e6a71" rx="8" ry="5" />
        <ellipse cx="80" cy="71" fill="#00a9e0" opacity="0.7" rx="4" ry="3" />

        {/* ── Landing gear ── */}
        <g fill="none" stroke="#5e6a71" strokeLinecap="round" strokeWidth="3">
          <line x1="70" x2="66" y1="72" y2="84" />
          <line x1="90" x2="94" y1="72" y2="84" />
          <line x1="62" x2="70" y1="84" y2="84" />
          <line x1="90" x2="98" y1="84" y2="84" />
        </g>
      </DroneSvg>
    </DroneWrap>
  );

  if (mode === 'page-content-with-background') {
    const target = document.getElementById('mainContent');
    const content = (
      <BackgroundOverlay {...rest}>
        <ThemedBackground
          $background={theme.getFullPageBoxBackground()}
          $backgroundSize={theme.getFullPageBoxBackgroundSize()}
        />
        <ContentCard>
          {message ? (
            <DroneMessageRow>
              {drone}
              <AnimatedMessage text={message} />
            </DroneMessageRow>
          ) : (
            drone
          )}
          {children}
        </ContentCard>
      </BackgroundOverlay>
    );
    if (target) {
      return ReactDOM.createPortal(content, target);
    }
    return content;
  }

  const content = (
    <Wrapper $mode={mode} {...rest}>
      {drone}
      {message && <Message>{message}</Message>}
      {children && <ChildrenWrap>{children}</ChildrenWrap>}
    </Wrapper>
  );

  if (mode === 'page-content') {
    const target = document.getElementById('mainContent');
    if (target) {
      return ReactDOM.createPortal(content, target);
    }
  }

  return content;
};

// ── Animations ─────────────────────────────────────────────────────────────

const rotorSpin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const hover = keyframes`
  0%   { transform: translateY(5px); }
  50%  { transform: translateY(-5px); }
  100% { transform: translateY(5px); }
`;

// ── Styled components ───────────────────────────────────────────────────────

const Wrapper = styled.div<{ $mode: 'fullscreen' | 'page-content' }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  background-color: #f4f6f8;

  ${({ $mode }) =>
    $mode === 'fullscreen'
      ? `
    position: fixed;
    inset: 0;
    z-index: 9999;
  `
      : `
    position: absolute;
    inset: 0;
  `}
`;

const DroneWrap = styled.div<{ $delay: string }>`
  animation: ${hover} ${HOVER_DURATION}ms ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay};
`;

const DroneSvg = styled.svg<{ $size: string }>`
  width: ${({ $size }) => $size};
  height: auto;
  overflow: visible;
`;

const RotorGroup = styled.g<{ $delay: string }>`
  animation: ${rotorSpin} ${ROTOR_DURATION}ms linear infinite;
  animation-delay: ${({ $delay }) => $delay};
`;

const ChildrenWrap = styled.div`
  width: 28rem;
  max-width: 90vw;
`;

const DroneMessageRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1.5rem;
  width: 100%;
`;

const Message = styled.p`
  font-family: 'Montserrat', sans-serif;
  font-size: 0.95rem;
  color: #37424a;
  letter-spacing: 0.03em;
  margin: 0;
`;

// ── Animated message (horizontal fade transition) ───────────────────────────

const textFadeOut = keyframes`
  from { opacity: 1; transform: translateX(0); }
  to   { opacity: 0; transform: translateX(-14px); }
`;

const textFadeIn = keyframes`
  from { opacity: 0; transform: translateX(14px); }
  to   { opacity: 1; transform: translateX(0); }
`;

type TextPhase = 'idle' | 'out' | 'in';

const AnimatedText = styled.p<{ $phase: TextPhase }>`
  font-family: 'Montserrat', sans-serif;
  font-size: 0.95rem;
  color: #37424a;
  letter-spacing: 0.03em;
  margin: 0;
  ${({ $phase }) =>
    $phase === 'out'
      ? css`
          animation: ${textFadeOut} 220ms ease forwards;
        `
      : $phase === 'in'
        ? css`
            animation: ${textFadeIn} 220ms ease forwards;
          `
        : ''}
`;

const AnimatedMessage = ({ text }: { text: string }) => {
  const [displayed, setDisplayed] = React.useState(text);
  const [phase, setPhase] = React.useState<TextPhase>('idle');
  const pending = React.useRef(text);

  React.useLayoutEffect(() => {
    if (text === displayed) {
      return;
    }
    pending.current = text;
    setPhase('out');
  }, [text, displayed]);

  return (
    <AnimatedText
      $phase={phase}
      onAnimationEnd={() => {
        if (phase === 'out') {
          setDisplayed(pending.current);
          setPhase('in');
        } else {
          setPhase('idle');
        }
      }}
    >
      {displayed}
    </AnimatedText>
  );
};

// ── page-content-with-background styled components ──────────────────────────

const BackgroundOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const ThemedBackground = styled.div<{ $background: string; $backgroundSize: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  background: ${({ $background }) => $background};
  background-size: ${({ $backgroundSize }) => $backgroundSize};
`;

const ContentCard = styled.div`
  position: relative;
  z-index: 1;
  width: 28rem;
  max-width: 90vw;
  padding: 1.3em;
  background-color: #fff;
  border-radius: 0.5em;
  box-shadow:
    0 3px 6px rgba(0, 0, 0, 0.16),
    0 3px 6px rgba(0, 0, 0, 0.23);
  font-family: 'Montserrat', sans-serif;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.5rem;
`;

export { DroneLoader };
